import React, { useState, useEffect, useCallback } from 'react';
import { FiX, FiTrash2, FiUpload } from 'react-icons/fi';
import { useAuthStore } from '../store/useAuthStore';
import { useStore } from '../store/useStore';
import '../styles/HeroEditor.css';

const API_BASE_URL = process.env.REACT_APP_API_URL;

export default function HeroEditor({ onClose }) {
  const token = useAuthStore((state) => state.token);
  const products = useStore((state) => state.products);
  const [heroMode, setHeroMode] = useState('bottom'); // 'top' or 'bottom'
  const [images, setImages] = useState([]);
  const [topImage, setTopImage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [editingImageId, setEditingImageId] = useState(null);
  const [editingProductId, setEditingProductId] = useState(null);

  const fetchHeroImages = useCallback(async () => {
    setLoading(true);
    try {
      if (heroMode === 'bottom') {
        const response = await fetch(`${API_BASE_URL}/api/hero-banner/bottom`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });

        if (response.ok) {
          const data = await response.json();
          setImages(Array.isArray(data) ? data : []);
        } else {
          setImages([]);
        }
      } else {
        const response = await fetch(`${API_BASE_URL}/api/hero-banner/top`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });

        if (response.ok) {
          const data = await response.json();
          setTopImage(data);
        } else {
          setTopImage(null);
        }
      }
    } catch (error) {
      console.error('Error fetching hero images:', error);
      setMessage('Error loading hero images');
    } finally {
      setLoading(false);
    }
  }, [token, heroMode]);

  useEffect(() => {
    fetchHeroImages();
  }, [fetchHeroImages]);

  const handleAddImage = async () => {
    const filesToUpload = heroMode === 'bottom' && selectedFiles.length > 0 ? selectedFiles : (selectedFile ? [selectedFile] : []);

    if (filesToUpload.length === 0) {
      setMessage('Please select image file(s)');
      return;
    }

    setSaving(true);
    setMessage('');
    setUploadProgress(0);

    try {
      const endpoint = heroMode === 'bottom'
        ? `${API_BASE_URL}/api/hero-banner/bottom/upload`
        : `${API_BASE_URL}/api/hero-banner/top/upload`;

      const uploadFile = (file, fileIndex) => {
        return new Promise((resolve, reject) => {
          const formData = new FormData();
          formData.append('file', file);
          formData.append('type', heroMode);

          const xhr = new XMLHttpRequest();

          xhr.upload.addEventListener('progress', (event) => {
            if (event.lengthComputable) {
              const fileProgress = Math.round((event.loaded / event.total) * 100);
              const totalProgress = Math.round(((fileIndex + fileProgress / 100) / filesToUpload.length) * 100);
              setUploadProgress(totalProgress);
            }
          });

          xhr.addEventListener('load', () => {
            if (xhr.status === 200) {
              resolve();
            } else {
              // Try to parse error response
              let errorMessage = `Upload failed: ${xhr.status}`;
              try {
                const response = JSON.parse(xhr.responseText);
                if (response.error) {
                  errorMessage = response.error;
                }
              } catch (e) {
                // If not JSON, use responseText directly
                if (xhr.responseText) {
                  errorMessage = xhr.responseText;
                }
              }
              reject(new Error(errorMessage));
            }
          });

          xhr.addEventListener('error', () => {
            reject(new Error('Upload error'));
          });

          xhr.open('POST', endpoint);
          xhr.setRequestHeader('Authorization', `Bearer ${token}`);
          xhr.send(formData);
        });
      };

      for (let fileIndex = 0; fileIndex < filesToUpload.length; fileIndex++) {
        const file = filesToUpload[fileIndex];
        await uploadFile(file, fileIndex);
      }

      setSelectedFile(null);
      setSelectedFiles([]);
      setUploadProgress(0);
      await fetchHeroImages();

      if (heroMode === 'bottom') {
        setMessage(`${filesToUpload.length} image(s) added to slideshow!`);
      } else {
        setMessage('Top hero image updated!');
      }
      setTimeout(() => setMessage(''), 3000);
      setSaving(false);
    } catch (error) {
      console.error('Error uploading images:', error);
      setMessage(`Error uploading images: ${error.message}`);
      setSaving(false);
      setUploadProgress(0);
    }
  };

  const handleDeleteImage = async (imageId) => {
    if (!window.confirm('Are you sure you want to delete this image?')) return;

    setSaving(true);
    setMessage('');

    try {
      const endpoint = heroMode === 'bottom'
        ? `${API_BASE_URL}/api/hero-banner/bottom/${imageId}`
        : `${API_BASE_URL}/api/hero-banner/top`;

      const response = await fetch(endpoint, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (response.ok) {
        await fetchHeroImages();
        setMessage('Image deleted successfully!');
        setTimeout(() => setMessage(''), 3000);
      } else {
        setMessage('Error deleting image');
      }
    } catch (error) {
      console.error('Error deleting image:', error);
      setMessage('Error deleting image');
    } finally {
      setSaving(false);
    }
  };

  const handleReorderImage = async (imageId, direction) => {
    setSaving(true);
    setMessage('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/hero-banner/bottom/${imageId}/reorder`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ direction }),
      });

      if (response.ok) {
        await fetchHeroImages();
        setMessage(`Image moved ${direction === 'up' ? 'up' : 'down'}!`);
        setTimeout(() => setMessage(''), 3000);
      } else {
        setMessage('Error reordering image');
      }
    } catch (error) {
      console.error('Error reordering image:', error);
      setMessage('Error reordering image');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateProductAssociation = async (imageId, productId) => {
    setSaving(true);
    setMessage('');

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/hero-banner/bottom/${imageId}/product`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ productId: productId || null }),
        }
      );

      if (response.ok) {
        await fetchHeroImages();
        setEditingImageId(null);
        setEditingProductId(null);
        const productName = productId
          ? products.find((p) => p.id === parseInt(productId))?.name || 'Product'
          : 'None';
        setMessage(`Product association updated to ${productName}!`);
        setTimeout(() => setMessage(''), 3000);
      } else {
        setMessage('Error updating product association');
      }
    } catch (error) {
      console.error('Error updating product association:', error);
      setMessage('Error updating product association');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="hero-editor-overlay">
      <div className="hero-editor-container">
        <div className="hero-editor-header">
          <h1>Edit Hero Images</h1>
          <button className="btn-close" onClick={onClose}>
            <FiX size={24} />
          </button>
        </div>

        {message && (
          <div className={`message ${message.includes('Error') ? 'error' : 'success'}`}>
            {message}
          </div>
        )}

        {/* Hero Mode Selector */}
        <div className="hero-mode-selector">
          <button
            className={`mode-btn ${heroMode === 'top' ? 'active' : ''}`}
            onClick={() => {
              setHeroMode('top');
              setMessage('');
              setSelectedFile(null);
            }}
          >
            Top Hero (Static Image)
          </button>
          <button
            className={`mode-btn ${heroMode === 'bottom' ? 'active' : ''}`}
            onClick={() => {
              setHeroMode('bottom');
              setMessage('');
              setSelectedFile(null);
            }}
          >
            Bottom Hero (Slideshow)
          </button>
        </div>

        <div className="hero-editor-content">
          {heroMode === 'top' ? (
            <>
              {/* Top Hero Editor */}
              <div className="add-image-section">
                <h3>Update Top Hero Image</h3>
                <p className="section-description">Replace the static hero image at the top of the home page</p>
                <div className="add-image-form file-upload-form">
                  <label htmlFor="top-hero-file" className="file-input-label">
                    <FiUpload size={20} />
                    {selectedFile && heroMode === 'top' ? selectedFile.name : 'Choose Image'}
                  </label>
                  <input
                    id="top-hero-file"
                    type="file"
                    accept="image/*"
                    onChange={(e) => setSelectedFile(e.target.files[0])}
                    disabled={saving}
                    className="file-input"
                  />
                  <button
                    onClick={handleAddImage}
                    disabled={saving || !selectedFile || uploadProgress > 0}
                    className="btn-add-image"
                  >
                    {uploadProgress > 0 ? `${uploadProgress}%` : <><FiUpload /> Upload Image</>}
                  </button>
                </div>
                {uploadProgress > 0 && (
                  <div className="progress-bar">
                    <div className="progress-fill" style={{ width: `${uploadProgress}%` }}></div>
                  </div>
                )}
              </div>

              <div className="images-list-section">
                <h3>Current Top Hero Image</h3>
                {loading ? (
                  <div className="loading">Loading image...</div>
                ) : topImage ? (
                  <div className="top-image-card">
                    <div className="top-image-preview">
                      <img src={topImage.image_url} alt="Top Hero" />
                    </div>
                    <div className="top-image-url">
                      <p>{topImage.image_url}</p>
                    </div>
                    <button
                      className="btn-delete-image"
                      onClick={() => handleDeleteImage(topImage.id)}
                      disabled={saving}
                      title="Delete this image"
                    >
                      <FiTrash2 /> Delete
                    </button>
                  </div>
                ) : (
                  <p className="no-images">No top hero image set. Add one above!</p>
                )}
              </div>
            </>
          ) : (
            <>
              {/* Bottom Hero Editor */}
              <div className="add-image-section">
                <h3>Add Slideshow Images</h3>
                <p className="section-description">Add one or more images to the rotating slideshow below products</p>
                <div className="add-image-form file-upload-form">
                  <label htmlFor="bottom-hero-file" className="file-input-label">
                    <FiUpload size={20} />
                    {selectedFiles.length > 0 ? `${selectedFiles.length} file(s) selected` : 'Choose Image(s)'}
                  </label>
                  <input
                    id="bottom-hero-file"
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(e) => setSelectedFiles(Array.from(e.target.files || []))}
                    disabled={saving}
                    className="file-input"
                  />
                  <button
                    onClick={handleAddImage}
                    disabled={saving || selectedFiles.length === 0 || uploadProgress > 0}
                    className="btn-add-image"
                  >
                    {uploadProgress > 0 ? `${uploadProgress}%` : <><FiUpload /> Add Images</>}
                  </button>
                </div>
                {uploadProgress > 0 && (
                  <div className="progress-bar">
                    <div className="progress-fill" style={{ width: `${uploadProgress}%` }}></div>
                  </div>
                )}
              </div>

              {/* Current Images */}
              <div className="images-list-section">
                <h3>Slideshow Images ({images.length})</h3>
                {loading ? (
                  <div className="loading">Loading images...</div>
                ) : images.length === 0 ? (
                  <p className="no-images">No slideshow images yet. Add one above to get started!</p>
                ) : (
                  <div className="images-list">
                    {images.map((image, idx) => (
                      <div key={image.id || idx} className="image-card">
                        <div className="image-preview">
                          <img src={image.image_url} alt={`Hero ${idx + 1}`} />
                          <div className="image-number">{idx + 1}</div>
                        </div>

                        <div className="image-product-section">
                          {editingImageId === image.id ? (
                            <div className="product-selector">
                              <select
                                value={editingProductId || ''}
                                onChange={(e) => setEditingProductId(e.target.value)}
                                className="product-select"
                              >
                                <option value="">No Product</option>
                                {products.map((product) => (
                                  <option key={product.id} value={product.id}>
                                    {product.name}
                                  </option>
                                ))}
                              </select>
                              <button
                                className="btn-save-product"
                                onClick={() =>
                                  handleUpdateProductAssociation(image.id, editingProductId)
                                }
                                disabled={saving}
                              >
                                Save
                              </button>
                              <button
                                className="btn-cancel-product"
                                onClick={() => {
                                  setEditingImageId(null);
                                  setEditingProductId(null);
                                }}
                                disabled={saving}
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <div className="product-display">
                              <span className="product-label">
                                Product:{' '}
                                {image.product_id
                                  ? products.find((p) => p.id === image.product_id)?.name ||
                                    'Unknown'
                                  : 'None'}
                              </span>
                              <button
                                className="btn-edit-product"
                                onClick={() => {
                                  setEditingImageId(image.id);
                                  setEditingProductId(image.product_id || '');
                                }}
                                disabled={saving}
                              >
                                Edit
                              </button>
                            </div>
                          )}
                        </div>

                        <div className="image-actions">
                          {idx > 0 && (
                            <button
                              className="btn-reorder"
                              onClick={() => handleReorderImage(image.id, 'up')}
                              disabled={saving}
                              title="Move up"
                            >
                              ▲ Up
                            </button>
                          )}
                          {idx < images.length - 1 && (
                            <button
                              className="btn-reorder"
                              onClick={() => handleReorderImage(image.id, 'down')}
                              disabled={saving}
                              title="Move down"
                            >
                              ▼ Down
                            </button>
                          )}
                          <button
                            className="btn-delete-image"
                            onClick={() => handleDeleteImage(image.id)}
                            disabled={saving}
                            title="Delete this image"
                          >
                            <FiTrash2 /> Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
